import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { AnalyticsFeeder } from "@/lib/api";
import { completedWatPeriod } from "@/lib/reporting-compose";

function watDay(instant: string, inclusiveEnd = false): string {
  const time = Date.parse(instant);
  return Number.isFinite(time) ? new Date(time + 3600000 - (inclusiveEnd ? 1 : 0)).toISOString().slice(0, 10) : "";
}

export function ReportingPeriodPicker({
  from, to, custom, onChange,
}: {
  from: string;
  to: string;
  custom: boolean;
  onChange: (range: { from_time: string; to_time: string }) => void;
}): JSX.Element {
  const [preset, setPreset] = useState(custom ? "custom" : "day");
  const [start, setStart] = useState(watDay(from));
  const [end, setEnd] = useState(watDay(to, true));
  return (
    <div className="space-y-3">
      <label className="block space-y-1 text-sm">
        <span>Report period</span>
        <select className="h-10 w-full rounded-md border bg-background px-3" value={preset}
          onChange={(event) => {
            const value = event.target.value;
            setPreset(value);
            if (value !== "custom") {
              const range = completedWatPeriod(value === "week" ? 7 : 1);
              setStart(watDay(range.from_time)); setEnd(watDay(range.to_time, true)); onChange(range);
            }
          }}>
          <option value="day">Previous completed day</option>
          <option value="week">Last 7 completed days</option>
          <option value="custom">Custom dates</option>
        </select>
      </label>
      {preset === "custom" ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="space-y-1 text-sm">First day (WAT)
            <Input type="date" value={start} onChange={(event) => {
              setStart(event.target.value);
              const value = Date.parse(`${event.target.value}T00:00:00+01:00`);
              onChange({ from_time: Number.isFinite(value) ? new Date(value).toISOString() : "", to_time: to });
            }} />
          </label>
          <label className="space-y-1 text-sm">Last day (WAT)
            <Input type="date" value={end} onChange={(event) => {
              setEnd(event.target.value);
              const value = Date.parse(`${event.target.value}T00:00:00+01:00`);
              onChange({ from_time: from, to_time: Number.isFinite(value) ? new Date(value + 86400000).toISOString() : "" });
            }} />
          </label>
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Completed days use West Africa Time (UTC+01:00). The last day is included in the selected calendar range.
      </p>
    </div>
  );
}

/** Search/filter operations never silently select, deselect, or broaden scope. */
export function ReportingFeederPicker({
  feeders, selected, onChange,
}: {
  feeders: readonly AnalyticsFeeder[];
  selected: string[];
  onChange: (ids: string[]) => void;
}): JSX.Element {
  const id = useId();
  const [search, setSearch] = useState("");
  const [disco, setDisco] = useState("");
  const [station, setStation] = useState("");
  const words = search.toLocaleLowerCase().trim().split(/\s+/).filter(Boolean);
  const results = feeders.filter((feeder) =>
    (!disco || feeder.disco === disco) && (!station || feeder.substation === station) &&
    words.every((word) => [feeder.feeder_name, feeder.substation, feeder.disco, feeder.dev_eui, feeder.device_id]
      .join(" ").toLocaleLowerCase().includes(word)));
  const matchingIds = results.map((feeder) => feeder.dev_eui);
  const merged = [...new Set([...selected, ...matchingIds])];
  const selectedNames = feeders.filter((feeder) => selected.includes(feeder.dev_eui));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-medium">{selected.length} feeders selected</p>
        {selected.length ? <Button type="button" size="sm" variant="ghost" onClick={() => onChange([])}>Clear selection</Button> : null}
      </div>
      {selectedNames.length ? <p className="line-clamp-2 text-xs text-muted-foreground">
        {selectedNames.map((feeder) => feeder.feeder_name).join(", ")}
      </p> : <p className="text-sm text-muted-foreground">Choose the feeders to include in this export.</p>}
      <details className="rounded-lg border">
        <summary className="cursor-pointer px-4 py-3 text-sm font-medium">Choose feeders</summary>
        <div className="space-y-3 border-t p-3">
          <Input aria-label="Search feeders" placeholder="Search by feeder, substation or device…" value={search}
            onChange={(event) => setSearch(event.target.value)} />
          <div className="grid gap-2 sm:grid-cols-2">
            <select aria-label="Filter feeders by DisCo" className="h-9 min-w-0 rounded-md border bg-background px-2 text-sm"
              value={disco} onChange={(event) => { setDisco(event.target.value); setStation(""); }}>
              <option value="">All DisCos</option>
              {[...new Set(feeders.map((feeder) => feeder.disco).filter(Boolean))].sort().map((value) => <option key={value}>{value}</option>)}
            </select>
            <select aria-label="Filter feeders by substation" className="h-9 min-w-0 rounded-md border bg-background px-2 text-sm"
              value={station} onChange={(event) => setStation(event.target.value)}>
              <option value="">All substations</option>
              {[...new Set(feeders.filter((feeder) => !disco || feeder.disco === disco).map((feeder) => feeder.substation).filter(Boolean))]
                .sort().map((value) => <option key={value}>{value}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
            <span>{results.length} matching · maximum 64 selected</span>
            <Button type="button" size="sm" variant="outline" disabled={!results.length || merged.length > 64}
              onClick={() => onChange(merged)}>Select matching feeders</Button>
          </div>
          <div className="max-h-56 space-y-1 overflow-y-auto" role="group" aria-label="Authorized feeders">
            {results.map((feeder, index) => (
              <label key={feeder.dev_eui} htmlFor={`${id}-${index}`} title={feeder.dev_eui}
                className="flex min-w-0 cursor-pointer items-start gap-3 rounded-md px-2 py-2 hover:bg-muted/50">
                <input id={`${id}-${index}`} type="checkbox" className="mt-1" checked={selected.includes(feeder.dev_eui)}
                  disabled={!selected.includes(feeder.dev_eui) && selected.length >= 64}
                  onChange={(event) => onChange(event.target.checked
                    ? [...selected, feeder.dev_eui] : selected.filter((value) => value !== feeder.dev_eui))} />
                <span className="min-w-0 text-sm">
                  <span className="block break-words">{feeder.feeder_name}</span>
                  <span className="block text-xs text-muted-foreground">{feeder.substation}{feeder.disco ? ` · ${feeder.disco}` : ""}</span>
                </span>
              </label>
            ))}
            {!results.length ? <p role="status" className="p-3 text-sm text-muted-foreground">No matching feeders. Your selection is unchanged.</p> : null}
          </div>
        </div>
      </details>
    </div>
  );
}
